import { EmbedOrganization } from "../../types/organizations";
import { OutreachEmailStatuses, ExtendedEmailStatuses } from "../../types/organizations";
import { DEFAULT_CUSTOM_FIELD_IDS } from "../consts";
import dayjs from "dayjs";

export interface OutreachQueryFilter {
  attribute: string;
  operator?: string;
  value: string[];
  metaData?: {
    childOperator?: string;
  };
}

/**
 * Calculates the number of days between two dates
 */
function getDaysDifference(startDate: Date, endDate: Date): number {
  const start = dayjs(startDate).startOf("day");
  const end = dayjs(endDate).endOf("day");
  return end.diff(start, "day");
}

/**
 * Creates date range filters for createdAt/updatedAt fields
 * Uses "between" operator for date ranges with ISO 8601 timestamps
 */
function getCreatedUpdatedDateFilters(
  startDate: Date,
  endDate: Date
): OutreachQueryFilter[] {
  // Format dates as ISO 8601 timestamps (YYYY-MM-DDTHH:mm:ss.SSSZ)
  // Start of day for startDate, end of day for endDate
  const startDateISO = dayjs(startDate).startOf("day").toISOString();
  const endDateISO = dayjs(endDate).endOf("day").toISOString();

  return [
    {
      attribute: "createdAt",
      operator: "between",
      value: [startDateISO, endDateISO],
    },
    {
      attribute: "updatedAt",
      operator: "between",
      value: [startDateISO, endDateISO],
    },
  ];
}

/**
 * Creates date range filters for Outreach
 * Uses a "between" operator for date ranges, which is standard for date range queries
 * Only works for Outreach (number fields), not HubSpot (string fields)
 */
function getDateFilters(
  timestampCustomField: number | string | null | undefined,
  startDate: Date,
  endDate: Date
): OutreachQueryFilter[] {
  // Only create filters for Outreach (numeric fields)
  // HubSpot uses different field names and query format
  if (typeof timestampCustomField !== 'number') {
    return [];
  }
  
  // Format dates as ISO strings (YYYY-MM-DD)
  const startDateISO = dayjs(startDate).startOf("day").format("YYYY-MM-DD");
  const endDateISO = dayjs(endDate).endOf("day").format("YYYY-MM-DD");

  // Use "between" operator for date range
  // If Outreach doesn't support "between", we may need to use two separate filters
  return [
    {
      attribute: `custom${timestampCustomField}`,
      operator: "between",
      value: [startDateISO, endDateISO],
    },
  ];
}

/**
 * Builds an Outreach prospect search URL with filters for email verification date and status
 * @param organization - Organization data containing custom field IDs
 * @param startDate - Start date of the time range
 * @param endDate - End date of the time range
 * @param status - Email status to filter by (Valid, Invalid, Questionable, or null for all)
 * @param isBouncesPrevented - If true, filter for bounces prevented (uses enriched custom field)
 * @returns Outreach prospect search URL
 */
export function buildOutreachProspectUrl(
  organization: EmbedOrganization | null,
  startDate: Date,
  endDate: Date,
  status: OutreachEmailStatuses | ExtendedEmailStatuses | null = null,
  isBouncesPrevented = false
): string {
  if (!organization) {
    return "https://web.outreach.io/prospects";
  }

  const filters: OutreachQueryFilter[] = [];

  // Special handling for NoEmail status - use createdAt/updatedAt and email isEmpty
  if (status === OutreachEmailStatuses.NoEmail) {
    // For Missing prospects, filter by:
    // 1. email isEmpty (must be first)
    // 2. createdAt within timeframe
    // 3. updatedAt within timeframe
    // Order matters - email.isEmpty first, then dates
    filters.push({
      attribute: "email",
      operator: "isEmpty",
      value: [],
    });
    
    const dateFilters = getCreatedUpdatedDateFilters(startDate, endDate);
    filters.push(...dateFilters);
    
    // Build query string
    const queryFilters = encodeURIComponent(JSON.stringify(filters));
    const url = `https://web.outreach.io/prospects?queryFilters=${queryFilters}&sortBy=createdAt&sortDirection=desc`;
    return url;
  }

  // Add date filters for email verification timestamp (only for Outreach)
  // HubSpot uses different field format, so skip if it's a string
  const timestampCustomField =
    organization.timestampCustomField ||
    DEFAULT_CUSTOM_FIELD_IDS.timestampCustomFieldId;
  
  // Only add date filters if timestampCustomField is a number (Outreach)
  if (typeof timestampCustomField === 'number') {
    const dateFilters = getDateFilters(timestampCustomField, startDate, endDate);
    filters.push(...dateFilters);
  }

  // Add status filter if specified (only for Outreach - HubSpot uses different format)
  // Only process if this is an Outreach organization (fields are numbers)
  const isOutreachOrg = organization.initialIntegrationType !== "hubspot";
  
  if (status && isOutreachOrg) {
    if (status === ExtendedEmailStatuses.EnrichedByBoring) {
      // For enriched by boring, filter by enriched custom field
      // The field value should be "Enriched by boring"
      const enrichedCustomField =
        (typeof organization.enrichedCustomField === 'number' ? organization.enrichedCustomField : null) ||
        DEFAULT_CUSTOM_FIELD_IDS.enrichedCustomFieldId;
      if (enrichedCustomField) {
        filters.push({
          attribute: `custom${enrichedCustomField}`,
          value: ["Enriched by boring"],
        });
      }
    } else if (isBouncesPrevented && status === ExtendedEmailStatuses.BouncesPrevented) {
      // For bounces prevented, we need filters:
      // 1. Email verification date within timeframe (already added above)
      // 2. Email status = Invalid (using email status custom field)
      // 3. Email opted out = true (only if organization has opt-out enabled)
      const customField =
        (typeof organization.customField === 'number' ? organization.customField : null) ||
        DEFAULT_CUSTOM_FIELD_IDS.emailStatusCustomFieldId;
      
      if (customField) {
        // Filter by Invalid status in the email status custom field
        filters.push({
          attribute: `custom${customField}`,
          value: ["Invalid"],
        });
      }

      // Only include emailsOptedOut filter if the organization has opt-out enabled
      // disableOptOutProspects = false/undefined means they DO opt out prospects
      if (!organization.disableOptOutProspects) {
        // Filter by email opted out = true (standard Outreach prospect field)
        // Note: The attribute is "emailsOptedOut" (plural) and requires "is" operator
        filters.push({
          attribute: "emailsOptedOut",
          operator: "is",
          value: ["true"],
        });
      }
    } else if (Object.values(OutreachEmailStatuses).includes(status as OutreachEmailStatuses)) {
      // For email status (Valid, Invalid, Questionable)
      // Note: NoEmail is handled separately above
      const customField =
        (typeof organization.customField === 'number' ? organization.customField : null) ||
        DEFAULT_CUSTOM_FIELD_IDS.emailStatusCustomFieldId;
      
      if (customField) {
        // Map status enum to display value (Valid, Invalid, or Questionable)
        const statusValue = 
          status === OutreachEmailStatuses.Valid ? "Valid" :
          status === OutreachEmailStatuses.Invalid ? "Invalid" :
          status === OutreachEmailStatuses.Questionable ? "Questionable" :
          status;

        filters.push({
          attribute: `custom${customField}`,
          value: [statusValue],
        });
      }
    }
  }

  // Build query string
  const queryFilters = encodeURIComponent(JSON.stringify(filters));
  const url = `https://web.outreach.io/prospects?queryFilters=${queryFilters}&sortBy=createdAt&sortDirection=desc`;

  return url;
}
